import React from "react";
import { Button } from "../ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { formatDraftTimestamp } from "../../lib/operationDraft";

// R5 — extraído MECANICAMENTE de pages/NewOperation.jsx (JSX verbatim; o
// estado vive no componente-pai e chega por `ctx`).
export default function RascunhoAvisos({ ctx }) {
  const {
    draftFound,
    draftBannerRef,
    confirmDiscard,
    setConfirmDiscard,
    exitTarget,
    setExitTarget,
    rascunhoDisponivel,
    draftNotice,
    restoreDraft,
    discardDraft,
    confirmExit,
  } = ctx;
  return (
    <>
      {/* FS-161(b) — rascunho encontrado. NUNCA restaura sozinho: repor
          valores de dinheiro sem o usuário pedir é a variante de UX do erro de
          ×100. A data diz DE QUANDO é o rascunho, para ele decidir.
          a11y: `region` nomeada (e não `status` — live region não é lugar de
          botão) e foco programático ao aparecer, no molde do card de bloqueio
          terminal desta mesma tela. Ignorar o banner e digitar já o dispensa
          (o efeito de U1 acima), então ele nunca vira uma parede. */}
      {draftFound && (
        <div
          role="region"
          aria-labelledby="draft-restore-title"
          tabIndex={-1}
          ref={draftBannerRef}
          className="rounded-md p-4 border border-blue-200 bg-blue-50 text-[13px] text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-300"
          data-testid="draft-restore-banner"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            {/* Round 3 / B2: a frase anterior ("o rascunho antigo sai da
                frente") lia como dispensar um aviso, não como APAGAR dados —
                e continuar preenchendo sobrescreve o rascunho em 800ms, sem
                desfazer. Enquanto o "Descartar" pede confirmação dizendo que
                não dá para desfazer, o caminho MAIS fácil de destruir era o
                mudo. A copy agora diz o que acontece, com as mesmas palavras. */}
            <div id="draft-restore-title">
              Encontramos um rascunho de{" "}
              <strong>{formatDraftTimestamp(draftFound.savedAt)}</strong> desta tela, com os dados
              que você tinha preenchido. Se continuar preenchendo sem restaurar,{" "}
              <strong>este rascunho é substituído pelo que você digitar</strong> — e isso não pode
              ser desfeito.
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                size="sm"
                className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
                onClick={restoreDraft}
                data-testid="draft-restore-btn"
              >
                Restaurar rascunho
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmDiscard(true)}
                data-testid="draft-discard-btn"
              >
                Descartar rascunho
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Round 2 / U6 — o rascunho trazia um produto que não está mais
          disponível. Sem este aviso o usuário voltaria à etapa 1 sem entender
          por que a escolha dele sumiu. Sai sozinho quando ele escolhe outro. */}
      {draftNotice && (
        <div
          role="alert"
          className="rounded-md p-3 border border-amber-200 bg-amber-50 text-[13px] text-amber-800"
          data-testid="draft-notice"
        >
          {draftNotice}
        </div>
      )}

      {/* Round 2 / U3 — descartar é destrutivo e não tem desfazer: confirma em
          2 passos, no molde do AlertDialog de remoção de usuário (Users.jsx). */}
      <AlertDialog open={confirmDiscard} onOpenChange={(o) => !o && setConfirmDiscard(false)}>
        <AlertDialogContent data-testid="discard-draft-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar o rascunho?</AlertDialogTitle>
            <AlertDialogDescription>
              Os dados guardados em{" "}
              <strong>{formatDraftTimestamp(draftFound?.savedAt)}</strong> serão apagados deste
              navegador. Não é possível desfazer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="discard-draft-dismiss-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={discardDraft}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="discard-draft-confirm-btn"
            >
              Descartar rascunho
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* FS-161(a) — saída por botão do wizard com formulário sujo. Molde do
          AlertDialog de remoção de usuário (pages/Users.jsx); AlertDialog e não
          Dialog (o do CancelOperationDialog) porque a pergunta pede uma escolha:
          não fecha por clique fora — ESC e os botões seguem fechando. */}
      <AlertDialog open={!!exitTarget} onOpenChange={(open) => { if (!open) setExitTarget(null); }}>
        <AlertDialogContent data-testid="leave-operation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Sair sem criar a operação?</AlertDialogTitle>
            {/* Round 2 / U2: a promessa é CONDICIONAL. Sem usuário identificado
                ou com o storage recusando (quota/modo privado), não há rascunho
                nenhum — prometer 24h seria mentir na hora exata em que o
                usuário decide se pode sair. */}
            <AlertDialogDescription data-testid="leave-operation-copy">
              {rascunhoDisponivel ? (
                <>
                  Você tem dados preenchidos que ainda não foram enviados. Eles ficam guardados como
                  rascunho nesta aba do navegador por 24 horas, e são oferecidos de volta quando
                  você voltar a esta tela.
                </>
              ) : (
                <>
                  Você tem dados preenchidos que ainda não foram enviados e{" "}
                  <strong>não foi possível guardar rascunho neste navegador</strong> — se sair
                  agora, será preciso preencher tudo de novo.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="leave-operation-dismiss-btn">
              Continuar preenchendo
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmExit} data-testid="leave-operation-confirm-btn">
              Sair da tela
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
